import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Contractor, PendingOfficeInvite, PermissionLevel } from '../shared/api';
import { supabaseConfigured } from '../shared/config';
import { ApiError, api } from './api';
import { signOut as supabaseSignOut, supabase } from './supabase';

/**
 * One place that knows who is signed in and what they may do.
 *
 * `contractor` comes from GET /api/me rather than the Supabase user, because
 * permission level and mail balance are backend state — the JWT only proves
 * identity.
 */

const RANK: Record<PermissionLevel, number> = { read_only: 0, field: 1, manager: 2, admin: 3 };

export interface SessionValue {
  loading: boolean;
  /** Null when signed out or when the token no longer resolves to an account. */
  contractor: Contractor | null;
  /** Addressed office invite. Login did not claim it. */
  pendingOfficeInvite: PendingOfficeInvite | null;
  /**
   * Whether this seat must sign the Early Access Agreement. Comes from the
   * backend, not from contractor.betaAgreementAcceptedAt: an invitee whose
   * office has accepted is covered while their own stub row is still null.
   */
  betaAgreementRequired: boolean;
  /**
   * The self-service liability disclaimer, verbatim from the backend. One
   * canonical copy: the sentence beside the checkbox has to be the sentence
   * the API refuses with, or the acknowledgment records assent to text nobody
   * can reconstruct.
   */
  selfServiceDisclaimer: string | null;
  /**
   * Whether LienGuardian mails and charges on the contractor's behalf. False
   * during the pilot (consequential actions off). Defaults to true when the
   * server doesn't say, so an older API never hides the send flow.
   */
  platformMailingEnabled: boolean;
  error: string | null;
  signedIn: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  /** True when the signed-in user is at or above `min`. */
  can: (min: PermissionLevel) => boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

export function meetsLevel(actual: PermissionLevel | undefined, min: PermissionLevel): boolean {
  if (!actual) return false;
  return RANK[actual] >= RANK[min];
}

export function SessionProvider({ children, initial }: { children: ReactNode; initial?: Partial<SessionValue> }) {
  const [contractor, setContractor] = useState<Contractor | null>(initial?.contractor ?? null);
  const [pendingOfficeInvite, setPendingOfficeInvite] = useState<PendingOfficeInvite | null>(
    initial?.pendingOfficeInvite ?? null,
  );
  const [betaAgreementRequired, setBetaAgreementRequired] = useState(initial?.betaAgreementRequired ?? false);
  const [selfServiceDisclaimer, setSelfServiceDisclaimer] = useState<string | null>(initial?.selfServiceDisclaimer ?? null);
  const [platformMailingEnabled, setPlatformMailingEnabled] = useState(initial?.platformMailingEnabled ?? true);
  const [loading, setLoading] = useState(initial?.loading ?? true);
  const [error, setError] = useState<string | null>(initial?.error ?? null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const me = await api.get<{
        contractor: Contractor;
        pendingOfficeInvite?: PendingOfficeInvite | null;
        betaAgreementRequired?: boolean;
        selfServiceDisclaimer?: string;
        platformMailingEnabled?: boolean;
      }>('/api/me');
      setContractor(me.contractor);
      setPendingOfficeInvite(me.pendingOfficeInvite ?? null);
      setBetaAgreementRequired(me.betaAgreementRequired ?? false);
      setSelfServiceDisclaimer(me.selfServiceDisclaimer ?? null);
      setPlatformMailingEnabled(me.platformMailingEnabled ?? true);
    } catch (err) {
      // 401 is the ordinary signed-out case, not something to shout about.
      if (err instanceof ApiError && err.status === 401) {
        setContractor(null);
        setPendingOfficeInvite(null);
        setBetaAgreementRequired(false);
      } else setError(err instanceof Error ? err.message : 'Could not load your account');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initial) return;
    let cancelled = false;
    void load();
    if (!supabaseConfigured) return;
    // Re-resolve the account whenever Supabase hands us a different session —
    // covers the magic link landing back in this tab.
    const { data } = supabase().auth.onAuthStateChange(() => {
      if (!cancelled) void load();
    });
    return () => {
      cancelled = true;
      data.subscription.unsubscribe();
    };
  }, [load, initial]);

  const signOut = useCallback(async () => {
    await supabaseSignOut();
    setContractor(null);
    setPendingOfficeInvite(null);
    setBetaAgreementRequired(false);
    setLoading(false);
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      loading,
      contractor,
      error,
      signedIn: !!contractor,
      refresh: load,
      signOut,
      can: (min) => meetsLevel(contractor?.permissionLevel, min),
      ...initial,
      pendingOfficeInvite: initial?.pendingOfficeInvite ?? pendingOfficeInvite,
      betaAgreementRequired: initial?.betaAgreementRequired ?? betaAgreementRequired,
      selfServiceDisclaimer: initial?.selfServiceDisclaimer ?? selfServiceDisclaimer,
      platformMailingEnabled: initial?.platformMailingEnabled ?? platformMailingEnabled,
    }),
    [loading, contractor, pendingOfficeInvite, betaAgreementRequired, selfServiceDisclaimer, platformMailingEnabled, error, load, signOut, initial],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside a SessionProvider');
  return ctx;
}
